import React, { useEffect, useRef, useState } from 'react';
import { createWidget } from '@typeform/embed';
import '@typeform/embed/build/css/widget.css';

interface TypeformEmbedProps {
  formId: string;
  className?: string;
  height?: string;
  minHeight?: string;
  hideHeaders?: boolean;
  hideFooter?: boolean;
  opacity?: number;
  buttonText?: string;
  onSubmit?: () => void;
  onClose?: () => void;
  hiddenFields?: Record<string, string>;
  tracking?: Record<string, string>;
}

const TypeformEmbed: React.FC<TypeformEmbedProps> = ({
  formId,
  className = '',
  height,
  minHeight = '600px',
  hideHeaders = false,
  hideFooter = false,
  opacity = 100,
  onSubmit,
  onClose,
  hiddenFields = {},
  tracking,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (containerRef.current && formId) {
      try {
        // Create the widget. hiddenFields, tracking, and callbacks are intentionally
        // excluded from the dependency array. The Typeform SDK provides no API to update
        // these after creation — re-running this effect destroys and re-creates the widget,
        // resetting the user back to question 1. Intentional restarts are controlled by
        // the parent via the React `key` prop.
        createWidget(formId, {
          container: containerRef.current,
          hideHeaders,
          hideFooter,
          opacity: opacity / 100,
          hidden: hiddenFields,
          tracking,
          onSubmit: () => {
            onSubmit?.();
          },
          onClose: () => {
            onClose?.();
          },
          onReady: () => {
            setIsLoading(false);
          },
        });
      } catch (error) {
        console.error('Error loading Typeform:', error);
        setHasError(true);
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, hideHeaders, hideFooter, opacity]);

  return (
    <div className={`w-full ${className}`}>
      {isLoading && (
        <div className='flex justify-center items-center py-8'>
          <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      )}

      {hasError && (
        <div className='bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-center'>
          <p>There was an error loading the form. Please try again later.</p>
          <button
            onClick={() => window.location.reload()}
            className='mt-2 bg-red-100 hover:bg-red-200 text-red-800 font-medium py-2 px-4 rounded transition-colors duration-200'
          >
            Retry
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        className={`w-full transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        style={{
          height: height || '100%',
          minHeight: minHeight,
        }}
      />
    </div>
  );
};

export default TypeformEmbed;
